import cloneDeep from 'lodash/cloneDeep';

import { mapCategory, mapSubCondition } from '@webapp/common/lib/map';
import { parseJSON, striptags, UnescapeHtml } from '@webapp/common/lib/utils';
import type {
    LogicParamModel,
    MaxDiffBlockItemModel,
    PageModel,
    QuestionAnswerModel,
    QuestionGroupModel,
    QuestionLogicModel,
    SurveyStore,
} from '@webapp/common/resources/entities';
import { initialPage, initialQuestionAnswer } from '@webapp/common/resources/entities';
import type { InfoDetailsModel, InfoModel } from '@webapp/common/resources/entities/info';
import type { QuestionEntity } from '@webapp/common/resources/entities/question';
import type { SurveyAddonAlert } from '@webapp/common/resources/entities/survey-addons/alert';
import { initialSurveyAddonAlert } from '@webapp/common/resources/entities/survey-addons/alert';
import type { SurveyAddonMain } from '@webapp/common/resources/entities/survey-addons/main';
import { initialSurveyAddonMain, normalizeTimerOn } from '@webapp/common/resources/entities/survey-addons/main';
import type { SurveyAddonParams } from '@webapp/common/resources/entities/survey-addons/params';
import type { SurveyAddonShare } from '@webapp/common/resources/entities/survey-addons/share';
import type { SurveyAddonTests } from '@webapp/common/resources/entities/survey-addons/tests';
import type { QuestionParams } from '@webapp/common/resources/question';
import type { QuestionType } from '@webapp/common/resources/survey';
import { IndicatorPosition, LogicType } from '@webapp/common/resources/survey';

import { isQuotaHideLogic } from './quota-action';

export const mapInfoParamsMain = (data: any = {}): SurveyAddonMain => ({
    ...data,
    recipientPhoneAuthType: data.recipientPhoneAuthType || initialSurveyAddonMain.recipientPhoneAuthType,
    // timerOn мог сохраниться объектом (наследие старых версий) — иначе таймер не стартует (FRONT-MAA)
    timerOn: normalizeTimerOn(data.timerOn),
    timer: data.timer || false,
    timerHours: parseInt(data.timerHours, 10) || 0,
    timerMinutes: parseInt(data.timerMinutes, 10) || 0,
    timerSeconds: parseInt(data.timerSeconds, 10) || 0,
    disabledSurveyText: data.disabledSurveyText ? UnescapeHtml(data.disabledSurveyText) : null,
    retryDisabledSurveyText: UnescapeHtml(
        data.retryDisabledSurveyText ? data.retryDisabledSurveyText : initialSurveyAddonMain.retryDisabledSurveyText,
    ),
    surveyWasBlocked: data.surveyWasBlocked ? UnescapeHtml(data.surveyWasBlocked) : null,
    completePageText: data.completePageText ? UnescapeHtml(data.completePageText) : null,
    quotaFilledText: data.quotaFilledText ? UnescapeHtml(data.quotaFilledText) : initialSurveyAddonMain.quotaFilledText,
    quotaFilledStartText: data.quotaFilledStartText
        ? UnescapeHtml(data.quotaFilledStartText)
        : initialSurveyAddonMain.quotaFilledStartText,
    completeSurvey: data.completeSurvey,
    completeSurveyUrl: data.completeSurveyUrl,
    welcomePageText: data.welcomePageText ? UnescapeHtml(data.welcomePageText) : null,
});

export const mapInfoParamsTests = (data: any = {}): SurveyAddonTests => ({
    maxScoreAmount: isNaN(parseFloat(data.maxScoreAmount)) ? null : parseFloat(data.maxScoreAmount),
    passingScore: isNaN(parseFloat(data.passingScore)) ? null : parseFloat(data.passingScore),
    testResultLabel: typeof data.testResultLabel === 'string' ? UnescapeHtml(data.testResultLabel) : null,
    passingScoreLabel: typeof data.passingScoreLabel === 'string' ? UnescapeHtml(data.passingScoreLabel) : null,
    testPassed: typeof data.testPassed === 'string' ? UnescapeHtml(data.testPassed) : null,
    testNotPassed: typeof data.testNotPassed === 'string' ? UnescapeHtml(data.testNotPassed) : null,
    scoreLabel: typeof data.scoreLabel === 'string' ? UnescapeHtml(data.scoreLabel) : null,
    textDiapasons: Array.isArray(data.textDiapasons) ? data.textDiapasons : [],
    calcResultType: data.calcResultType,
});

export const mapInfoParamsShare = (data: any = {}): SurveyAddonShare => ({
    download: Boolean(data && data.download),
    filters: Boolean(data && data.filters),
    answers: Boolean(data && data.answers),
    reports: Boolean(data && data.reports),
    cross: Boolean(data && data.cross),
});

export const mapInfoDetails = (data: any = {}): InfoDetailsModel => ({
    answer_bool: data.ANSWER_BOOL,
    answer_group_id: data.ANSWER_GROUP_ID,
    answer_id: data.ANSWER_ID,
    answer_int: data.ANSWER_INT,
    answer_str: data.ANSWER_STR,
    answer_text: data.ANSWER_TEXT,
    comment: data.COMMENT,
    expired: data.EXPIRED,
    file_id: data.FILE_ID,
    question_id: data.QUESTION_ID,
    cant_rate: data.CANT_RATE,
});

export const mapInfoOther = (data: any = {}): SurveyAddonParams => ({
    ...data,
    indicatorPosition: data.indicatorPosition || IndicatorPosition.BOTTOM,
});
const striptagsforall = <T extends object>(obj: T): T => {
    return Object.keys(obj).reduce((acc, key) => ({ ...acc, [key]: striptags(obj[key]) }), {}) as T;
};

// Тексты алертов дополняем дефолтами: у опросов, сохранённых до появления
// новых полей, в params.alert их нет.
export const mapAlert = (data: any = {}): SurveyAddonAlert => ({
    ...initialSurveyAddonAlert,
    ...striptagsforall(parseJSON(data, {})),
});
export const mapInfoShorted = (data: any = {}): Pick<InfoModel, 'params' | 'design' | 'title'> => {
    const $params = data.rows;
    return {
        title: striptags(data.TITLE),
        params: {
            alert: mapAlert($params.alert),
            main: mapInfoParamsMain(parseJSON($params.main)),
            other: mapInfoOther(parseJSON($params.params)),
            tests: mapInfoParamsTests(parseJSON($params.tests)),
            share: mapInfoParamsShare(parseJSON($params.share)),
        },
        design: {
            brand: parseJSON($params.brand),
            buttons: striptagsforall(parseJSON($params.buttons)),
            css: parseJSON($params.css || '{}'),
            fonts: parseJSON($params.fonts),
            view: parseJSON($params.view),
        },
    };
};
export const mapInfo = (data: any = {}): InfoModel => {
    const { $addons, $params, ID, template, TITLE, savingNotComplete, DEFAULT_LANG, MULTI_LANG } = data.rows;

    return {
        id: ID,
        title: striptags(TITLE),
        complete: data.complete || false,
        newToken: data.newToken || null,
        template,
        hideFooter: Boolean($addons && $addons.hideFooter),
        params: {
            alert: mapAlert($params.alert),
            main: mapInfoParamsMain(parseJSON($params.main)),
            other: mapInfoOther(parseJSON($params.params)),
            tests: mapInfoParamsTests(parseJSON($params.tests)),
            share: mapInfoParamsShare(parseJSON($params.share)),
        },
        design: {
            brand: parseJSON($params.brand),
            buttons: striptagsforall(parseJSON($params.buttons, {})),
            css: parseJSON($params.css || '{}'),
            fonts: parseJSON($params.fonts),
            view: parseJSON($params.view),
        },
        details: data.details?.map(mapInfoDetails),
        shareLink: data.shareLink,
        savingNotComplete,
        authHash: data.authHash,
        preservePhone: data.preservePhone,
        multiLang: MULTI_LANG,
        defaultLang: DEFAULT_LANG,
    };
};

export const mapQuestionAnswer = (data: any = {}): QuestionAnswerModel => {
    let value = [data.ANSWER];
    const withFile = data.FILE_PATH || data.FILE_ID;

    try {
        value = JSON.parse(data.ANSWER).map((label) => (label && String(label)) || '');
    } catch (e) {}

    if (withFile) {
    }

    return {
        ...cloneDeep(initialQuestionAnswer),
        id: data.ID,
        forwardParent: data.FORWARD_PARENT,
        value,
        pinned: data.PINNED,
        excluded: data.EXCLUDED,
        type: data.TYPE && Number(data.TYPE), // TODO fix backend pass number core#121
        exception: Boolean(data.EXCEPTION),
        videoLink: data.VIDEO_LINK,
        textBlock: data.TEXT_BLOCK,
        hidden: Boolean(data.hidden),
        hiddenReason: data.hiddenReason ?? null,
        quotaHidden: Boolean(data.hidden) && data.hiddenReason === 'QUOTA',
        answerArea: data.ANSWER_AREAS
            ? {
                  answerId: data.ANSWER_AREAS.ANSWER_ID,
                  height: data.ANSWER_AREAS.HEIGHT,
                  id: data.ANSWER_AREAS.ID,
                  isDefault: !!data.ANSWER_AREAS.IS_DEFAULT,
                  width: data.ANSWER_AREAS.WIDTH,
                  x: data.ANSWER_AREAS.X,
                  y: data.ANSWER_AREAS.Y,
              }
            : null,
        file: withFile
            ? {
                  id: data.FILE_ID,
                  path: data.FILE_PATH,
                  name: data.FILE_NAME, // TODO check
                  width: data.IMAGE_WIDTH,
                  height: data.IMAGE_HEIGHT,
                  mimeType: data.MIME_TYPE,
              }
            : null,
    };
};

export const mapQuestionGroup = ({
    ANSWERS,
    EXCEPTION,
    ID,
    NAME,
    TYPE,
    NUMERIC_VALUE,
    PINNED,
}: {
    ANSWERS: string;
    EXCEPTION: number;
    PINNED: number;
    ID: number;
    NAME: string;
    TYPE: string;
    NUMERIC_VALUE: number;
}): QuestionGroupModel => {
    let answers: Array<string> = [];

    try {
        answers = ANSWERS ? JSON.parse(ANSWERS) : answers;
    } catch (e) {
        console.error(e);
    }

    return {
        id: ID,
        name: NAME,
        exception: Boolean(EXCEPTION),
        pinned: Boolean(PINNED),
        type: TYPE && Number(TYPE), // TODO fix backend, pass number  core#121
        answers,
        numericValue: NUMERIC_VALUE,
    };
};

export const mapQuestionLogic = (data: any = {}): QuestionLogicModel => {
    const parsedParams = data.PARAMS
        ? parseJSON<
              Array<LogicParamModel> | { variants?: Array<LogicParamModel>; minCount?: number; maxCount?: number }
          >(data.PARAMS, [])
        : [];
    const isParamsArray = Array.isArray(parsedParams);

    return {
        id: data.ID,
        type: data.TYPE,
        action: data.TRANSITION_ACTION,
        transition: data.GOAL,
        transitionType: data.TRANSITION_TYPE,
        boolFunc: data.BOOL_FUNC,
        toQuestion: data.NEXT_QUESTION,
        toQuestions: data.NEXT_QUESTIONS ? parseJSON(data.NEXT_QUESTIONS, []) : ([] as any),
        toPages: data.NEXT_PAGES ? parseJSON(data.NEXT_PAGES, []) : ([] as any),
        answersToHide: data.ANSWERS_TO_HIDE ? parseJSON(data.ANSWERS_TO_HIDE, []) : [],
        toSurvey: data.TO_SURVEY,
        linkParamsLogicType: data.LINK_PARAMS_LOGIC_TYPE,
        toSurveyUrl: data.REWRITE,
        toPage: data.NEXT_PAGE,
        toWebsite: data.TO_WEB,
        completeText: data.COMPLETE_TEXT ? UnescapeHtml(data.COMPLETE_TEXT) : null,
        disqualText: data.BREAK_TEXT ? UnescapeHtml(data.BREAK_TEXT) : null,
        params: isParamsArray ? parsedParams : parsedParams.variants ?? [],
        minCount: isParamsArray ? undefined : parsedParams.minCount,
        maxCount: isParamsArray ? undefined : parsedParams.maxCount,
        linkId: null,
        linkValue: null,
        afterQuestionId: data.AFTER_QUESTION_ID,
        finishTransition: data.FINISH_TRANSITION,
        finishTransitionVariant: data.FINISH_TRANSITION_VARIANT,
        conditions: Array.isArray(data.conditions) ? data.conditions.map(mapSubCondition) : [],
        isExtended: data.IS_EXTENDED,
    };
};

const mapPairComparisonParam = ({
    ANSWER1_ANSWER,
    ANSWER1_FILE_PATH,
    ANSWER1_ID,
    ANSWER1_ORDER,
    ANSWER1_TEXT_BLOCK,
    ANSWER1_TYPE,
    ANSWER2_ANSWER,
    ANSWER2_FILE_PATH,
    ANSWER2_ID,
    ANSWER2_ORDER,
    ANSWER2_TEXT_BLOCK,
    ANSWER2_TYPE,
    PAIR_NUMBER,
}) => {
    return {
        answer1_answer: ANSWER1_ANSWER,
        answer1_filePath: ANSWER1_FILE_PATH,
        answer1_id: ANSWER1_ID,
        answer1_order: ANSWER1_ORDER,
        answer1_textBlock: ANSWER1_TEXT_BLOCK,
        answer1_type: ANSWER1_TYPE,

        answer2_answer: ANSWER2_ANSWER,
        answer2_filePath: ANSWER2_FILE_PATH,
        answer2_id: ANSWER2_ID,
        answer2_order: ANSWER2_ORDER,
        answer2_textBlock: ANSWER2_TEXT_BLOCK,
        answer2_type: ANSWER2_TYPE,

        pairNumber: PAIR_NUMBER,
    };
};

export const mapQuestion = ({
    answers,
    groups,
    categories,
    ID,
    logics,
    PAGE,
    parameters,
    forwardLogic,
    QUESTION,
    PINNED,
    QUESTION_TYPE_ID,
    selectedAnswerId,
    PAIR_COMPARISON_PARAMS,
    ORDER,
    FILE_PATH,
    conjointConfig,
    closedQuotas,
}: any = {}): Partial<QuestionEntity> => {
    const params = parseJSON<QuestionParams>(parameters.params, {});
    // TODO webapp#583
    const minLength = !!params.minLength ? parseInt(params.minLength as any, 10) || 0 : undefined;
    const maxLength = !!params.maxLength ? parseInt(params.maxLength as any, 10) || 0 : undefined;

    const mappedAnswers = answers.map(mapQuestionAnswer);
    const mappedLogics = logics.map(mapQuestionLogic);

    // Бэк может помечать hidden у закрытых квот любого действия. Скрываем
    // варианты только для ANSWERS_HIDE: у дисквала/завершения вариант должен
    // остаться видимым, а действие применяется в момент выбора (claim denied).
    // Действие ищем по logicId среди логик вопроса, а не берём из ответа
    // сервиса: в closedQuotas action не приходит, а transition_type может
    // остаться от прежнего действия.
    const hideAnswerIds = new Set<number>();
    (Array.isArray(closedQuotas) ? closedQuotas : []).forEach((closed: any) => {
        const logic = mappedLogics.find((l) => Number(l.id) === Number(closed?.logicId));
        if (!logic || logic.type !== LogicType.TYPE_QUESTION_QUOTA || !isQuotaHideLogic(logic)) {
            return;
        }
        (closed.answerIds || []).forEach((id: number) => hideAnswerIds.add(Number(id)));
    });
    mappedAnswers.forEach((answer) => {
        if (hideAnswerIds.has(answer.id)) {
            // Вариант закрыт по квоте со скрытием: прячем его сами, даже если
            // бэк не пометил ответ hidden (тогда же проставляем причину).
            if (!answer.hidden) {
                answer.hidden = true;
                answer.hiddenReason = 'QUOTA';
                answer.quotaHidden = true;
            }
        } else if (answer.quotaHidden) {
            answer.hidden = false;
            answer.hiddenReason = null;
            answer.quotaHidden = false;
        }
    });

    return {
        id: ID,
        title: QUESTION ? UnescapeHtml(QUESTION) : '',
        page: PAGE,
        forwardLogic,
        type: QUESTION_TYPE_ID as QuestionType,
        answers: mappedAnswers,
        groups: groups.map(mapQuestionGroup),
        logics: mappedLogics,
        filePath: FILE_PATH,
        order: ORDER,
        pinned: PINNED,
        categories: categories?.map(mapCategory),
        conjointConfig,
        params: {
            ...params,
            minLength,
            maxLength,
            timer: params.timer || false,
            hours: parseInt(params.hours as any, 10) || 0,
            minutes: parseInt(params.minutes as any, 10) || 0,
            seconds: parseInt(params.seconds as any, 10) || 0,
            helpText: params.helpText ? UnescapeHtml(params.helpText) : null,
            additionalText: params.additionalText ? UnescapeHtml(params.additionalText) : null,
            agreementLink: params.agreementLink ? UnescapeHtml(params.agreementLink) : null,
            agreementLeftLabel: params.agreementLeftLabel ? UnescapeHtml(params.agreementLeftLabel) : null,
            agreementRightLabel: params.agreementRightLabel ? UnescapeHtml(params.agreementRightLabel) : null,
            textBlock: params.textBlock ? UnescapeHtml(params.textBlock) : null,
            amount: params.amount && Number(params.amount),
            size: params.size && Number(params.size),
            // Вопросы «Рейтинг»/CSI, сохранённые до появления настройки «Диапазон от …», могли не содержать
            // minAmount — раньше шкала всегда начиналась с 1. Без дефолта прохождение стартует с 0 (0..amount-1),
            // хотя редактор подставляет 1 из initialQuestionParams. Явный «от 0» (minAmount: 0) сохраняем.
            minAmount: params.minAmount ?? 1,
        },
        selectedAnswerId,
        ...(PAIR_COMPARISON_PARAMS ? { pairComparisonParams: PAIR_COMPARISON_PARAMS.map(mapPairComparisonParam) } : {}),
    };
};

export const mapLinkParamsLogic = (data: any = {}): QuestionLogicModel => {
    const params = JSON.parse(data.PARAMS);

    return {
        id: data.ID,
        action: data.TRANSITION_ACTION,
        type: data.TYPE,
        transition: data.GOAL,
        transitionType: data.TRANSITION_TYPE,
        boolFunc: data.BOOL_FUNC,
        toQuestion: data.NEXT_QUESTION,
        toQuestions: data.NEXT_QUESTIONS ? parseJSON(data.NEXT_QUESTIONS, []) : ([] as any),
        toPages: data.NEXT_PAGES ? parseJSON(data.NEXT_PAGES, []) : ([] as any),
        answersToHide: data.ANSWERS_TO_HIDE ? parseJSON(data.ANSWERS_TO_HIDE, []) : [],
        toSurvey: data.TO_SURVEY,
        toSurveyUrl: data.REWRITE,
        toPage: data.NEXT_PAGE,
        toWebsite: data.TO_WEB,
        completeText: data.COMPLETE_TEXT ? UnescapeHtml(data.COMPLETE_TEXT) : null,
        disqualText: data.BREAK_TEXT ? UnescapeHtml(data.BREAK_TEXT) : null,
        params: undefined,
        linkParamsLogicType: data.LINK_PARAMS_LOGIC_TYPE,
        linkId: data.LINK_PARAM_ID,
        linkValue: params.linkParamValue,
        afterQuestionId: data.AFTER_QUESTION_ID,
        finishTransition: data.FINISH_TRANSITION,
        finishTransitionVariant: data.FINISH_TRANSITION_VARIANT,
        conditions: Array.isArray(data.CONDITIONS) ? data.CONDITIONS.map(mapSubCondition) : [],
        isExtended: data.IS_EXTENDED,
    };
};

export const mapQuestions = (survey: SurveyStore, questions: any = {}): Array<QuestionEntity> =>
    questions.map(mapQuestion);

export const mapPage = ({
    ID,
    QUESTION_RANDOM_ORDERING,
    QUESTION_SP,
    TITLE,
    PAGE_RANDOM_ORDERING_FIXED,
}: any = {}): Partial<PageModel> => ({
    ...cloneDeep(initialPage),
    id: ID,
    title: TITLE || '',
    onebyone: Boolean(QUESTION_SP),
    random: Boolean(QUESTION_RANDOM_ORDERING),
    randomFixed: Boolean(PAGE_RANDOM_ORDERING_FIXED),
});

export const mapPages = (pages: any = {}): Array<PageModel> => pages.map(mapPage);

export const mapMaxDiffBlock = (
    blocks: Array<{
        answerId: number;
        position: number;
        answer: string; // "[\"<p>one<\\/p>\",null,null]",
        textBlock: string | null;
        order: number;
        filePath: string | null;
    }>,
): Array<MaxDiffBlockItemModel> => {
    return blocks.map((block) => ({
        answerId: block.answerId,
        position: block.position,
        value1: parseJSON(block.answer, [])?.[0] || '',
        textBlock: block.textBlock,
        order: block.order,
        filePath: block.filePath,
    }));
};
